import React, { useState, useEffect } from 'react';
import { Lock, Unlock, PenTool, Terminal, Paperclip, Trash2, Send, History, CheckCircle, AlertCircle, Save, Rocket, Link, Plus, UploadCloud, Bell, Settings, Users, Clock } from 'lucide-react';
import { timeAgo, ACCENT_LIGHT, initials } from './App';

const Field = ({label, children, T}) => (
  <div>
    <div style={{fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8}}>{label}</div>
    {children}
  </div>
);

export default function NL2Tab({ ctx }) {
  const { 
    T, iS, lang, currentUser, isAdmin,
    nl2Reservation, nl2Log, nl2ReleaseNote, setNl2ReleaseNote, 
    nl2LogText, setNl2LogText, nl2LogType, setNl2LogType, 
    nl2Reserve, nl2Release, nl2AddLog, nl2DeleteLog,
    nl2BetaForm, setNl2BetaForm, nl2ReleaseBeta, nl2Uploading,
    users, teams, betaTeamOverride, setBetaTeamOverride, api, notify
  } = ctx;

  const isEN = lang === "en";

  // --- STATES ---
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [defaultTeamIds, setDefaultTeamIds] = useState([]);
  const [discordChannel, setDiscordChannel] = useState("");
  const [workingTeamId, setWorkingTeamId] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [fullSettings, setFullSettings] = useState({});
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    loadCurrentSettings();
    // Stelle sicher, dass die Standard-Kategorie gültig ist (falls noch 'manual' im State hing)
    if (!['modelling', 'scripting', 'others'].includes(nl2LogType)) {
      setNl2LogType('others');
    }
    return () => clearInterval(timer);
  }, []);

  const loadCurrentSettings = async () => {
    try {
      const res = await api('/nl2/settings');
      setFullSettings(res || {});
      setDiscordChannel(res?.discord_channel_id || "");
      setWorkingTeamId(res?.working_team_id || null);
    } catch(e) {}
  };

  const getDuration = (start) => {
    if (!start) return "";
    const diff = Math.floor((new Date() - new Date(start)) / 60000);
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    if (h > 0) return `${h} Std. ${m} Min.`;
    return `${m} Min.`;
  };

  const isReservedByMe = nl2Reservation?.user_id === currentUser?.id;
  const isReserved = !!nl2Reservation;
  const canWork = isAdmin || !workingTeamId || (teams.find(tm => tm.id === parseInt(workingTeamId))?.memberIds || []).includes(currentUser.id);

  const currentLinks = nl2BetaForm?.links || [{ label: "Download", url: "" }];
  const currentOverrides = betaTeamOverride || [];

  const openSettingsModal = async () => {
    setShowSettingsModal(true);
    setIsLoadingSettings(true);
    try {
      const [teamRes, settingsRes] = await Promise.all([api('/nl2/beta-team'), api('/nl2/settings')]);
      setDefaultTeamIds(teamRes || []);
      setFullSettings(settingsRes || {});
      setDiscordChannel(settingsRes?.discord_channel_id || "");
      setWorkingTeamId(settingsRes?.working_team_id || null);
    } catch(e) { notify("Fehler: " + e.message); } 
    finally { setIsLoadingSettings(false); }
  };

  const saveSettings = async () => {
    try {
      await api('/nl2/beta-team', { method: "PUT", body: JSON.stringify({ userIds: defaultTeamIds }) });
      await api('/nl2/settings', { 
        method: "PUT", 
        body: JSON.stringify({ ...fullSettings, discord_channel_id: discordChannel, working_team_id: workingTeamId }) 
      });
      notify("Einstellungen gespeichert!");
      setShowSettingsModal(false);
      loadCurrentSettings(); 
    } catch (e) { notify("Fehler: " + e.message); }
  };

  // 🚀 CLEVERE TEAM-AUSWAHL: Schaltet das gesamte Team auf einmal an oder ab
  const toggleEntireTeam = (team, currentList, setList) => {
    if (!team.memberIds || team.memberIds.length === 0) return notify(isEN ? "Team is empty!" : "Team hat keine Mitglieder!");
    const allSelected = team.memberIds.every(id => currentList.includes(id));
    if (allSelected) {
       setList(currentList.filter(id => !team.memberIds.includes(id))); // Alle entfernen
    } else {
       setList([...new Set([...currentList, ...team.memberIds])]); // Alle hinzufügen
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 20 }}>
      
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        
        {/* Status & Reservierung */}
        <div style={{ background: T.card, border: `1px solid ${isReserved ? (isReservedByMe ? "#3B82F650" : "#EF444450") : "#34D39950"}`, borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "20px 24px", background: isReserved ? (isReservedByMe ? "#3B82F615" : "#EF444415") : "#34D39915", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: isReserved ? (isReservedByMe ? "#3B82F6" : "#EF4444") : "#34D399", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
              {isReserved ? <Lock size={24} /> : <Unlock size={24} />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>
                {isReserved ? `Gesperrt von ${nl2Reservation.user_name}` : "Projekt verfügbar"}
              </div>
              <div style={{ fontSize: 13, color: T.textMuted, display: "flex", alignItems: "center", gap: 5 }}>
                {isReserved ? (
                  <><Clock size={14} color={ACCENT_LIGHT} /> Aktiv seit {getDuration(nl2Reservation.created_at || nl2Reservation.reserved_at)}</>
                ) : "Bereit für Bearbeitung"}
              </div>
            </div>
          </div>

          <div style={{ padding: 24 }}>
            {!isReserved ? (
              canWork ? (
                <button type="button" onClick={nl2Reserve} style={{ width: "100%", background: ACCENT_LIGHT, color: "#0A0D15", border: "none", padding: "14px", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: `0 8px 24px ${ACCENT_LIGHT}40`, transition: "transform 0.2s" }} className="hov">
                  <Lock size={18} /> Sperren & Arbeiten
                </button>
              ) : (
                <div style={{ padding: 16, background: T.cardHover, borderRadius: 12, border: `1px solid ${T.border2}`, color: T.textFaint, fontSize: 13, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <AlertCircle size={16} /> Dein Team ist nicht berechtigt, das Projekt zu sperren.
                </div>
              )
            ) : (
              isReservedByMe ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.textSub, display: "flex", alignItems: "center", gap: 6 }}>
                    <Save size={16} color={ACCENT_LIGHT} /> Was hast du geändert?
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ position: "relative" }}><div style={{ position: "absolute", top: 12, left: 12, color: T.textFaint }}><PenTool size={16}/></div><textarea value={nl2ReleaseNote.modelling} onChange={e => setNl2ReleaseNote({...nl2ReleaseNote, modelling: e.target.value})} placeholder="Modelling..." style={{ ...iS, paddingLeft: 38, resize: "vertical", minHeight: 44 }} rows={1} /></div>
                    <div style={{ position: "relative" }}><div style={{ position: "absolute", top: 12, left: 12, color: T.textFaint }}><Terminal size={16}/></div><textarea value={nl2ReleaseNote.scripting} onChange={e => setNl2ReleaseNote({...nl2ReleaseNote, scripting: e.target.value})} placeholder="Scripting..." style={{ ...iS, paddingLeft: 38, resize: "vertical", minHeight: 44 }} rows={1} /></div>
                    <div style={{ position: "relative" }}><div style={{ position: "absolute", top: 12, left: 12, color: T.textFaint }}><Paperclip size={16}/></div><textarea value={nl2ReleaseNote.others} onChange={e => setNl2ReleaseNote({...nl2ReleaseNote, others: e.target.value})} placeholder="Sonstiges..." style={{ ...iS, paddingLeft: 38, resize: "vertical", minHeight: 44 }} rows={1} /></div>
                  </div>
                  <button type="button" onClick={nl2Release} style={{ width: "100%", background: "#34D399", color: "#0A0D15", border: "none", padding: "14px", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: `0 8px 24px rgba(52,211,153,0.3)` }} className="hov">
                    <Unlock size={18} /> Projekt freigeben
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12, background: T.cardHover, padding: 16, borderRadius: 12, border: `1px solid ${T.border2}` }}>
                  <AlertCircle color="#F59E0B" size={24} />
                  <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5 }}>
                    Du kannst das Projekt nicht bearbeiten, da {nl2Reservation.user_name} gerade daran arbeitet.
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* BETA UPLOAD KARTE */}
        {isAdmin && (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", background: "rgba(139, 92, 246, 0.1)", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}><Rocket size={20} color="#8B5CF6" /><span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Beta-Release veröffentlichen</span></div>
              <button type="button" onClick={openSettingsModal} style={{ background: "transparent", border: "none", color: "#8B5CF6", cursor: "pointer", padding: 4 }}><Settings size={18} /></button>
            </div>
            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label="ROADMAP VERSION" T={T}>
  <select value={nl2BetaForm?.version || ""} onChange={e => setNl2BetaForm({...nl2BetaForm, version: e.target.value})} style={iS}>
    <option value="">-- Version wählen --</option>
    {(ctx.versions || []).map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
  </select>
</Field>
              <Field label="CHANGELOG" T={T}><textarea value={nl2BetaForm?.changelog || ""} onChange={e => setNl2BetaForm({...nl2BetaForm, changelog: e.target.value})} rows={3} style={{...iS, resize:"vertical"}} /></Field>
              
              <Field label="DOWNLOAD LINKS" T={T}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {currentLinks.map((link, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Link size={16} color={T.textMuted} />
                      <input value={link.label} onChange={e => { const n = [...currentLinks]; n[idx].label = e.target.value; setNl2BetaForm({...nl2BetaForm, links: n}); }} placeholder="Name (z.B. Mirror)" style={{ ...iS, width: 140 }} />
                      <input value={link.url} onChange={e => { const n = [...currentLinks]; n[idx].url = e.target.value; setNl2BetaForm({...nl2BetaForm, links: n}); }} placeholder="https://..." style={{ ...iS, flex: 1 }} />
                      {currentLinks.length > 1 && (
                        <button type="button" onClick={() => setNl2BetaForm({...nl2BetaForm, links: currentLinks.filter((_, i) => i !== idx)})} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer", padding: "0 8px" }}><Trash2 size={16} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => setNl2BetaForm({...nl2BetaForm, links: [...currentLinks, {label: "Download", url: ""}]})} style={{ background: "transparent", border: `1px dashed ${T.border3}`, color: T.textMuted, padding: "8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 4 }}>
                    <Plus size={14} /> Weiteren Link hinzufügen
                  </button>
                </div>
              </Field>

              {/* TEAMS UND NUTZER BENACHRICHTIGEN */}
              <Field label="ZUSÄTZLICH BENACHRICHTIGEN" T={T}>
                {teams && teams.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    {teams.map(team => {
                      const allSelected = team.memberIds && team.memberIds.length > 0 && team.memberIds.every(id => currentOverrides.includes(id));
                      return (
                        <button type="button" key={team.id} onClick={() => toggleEntireTeam(team, currentOverrides, setBetaTeamOverride)} style={{ padding: "6px 12px", borderRadius: 8, background: allSelected ? team.color : team.color + "20", color: allSelected ? "#fff" : team.color, border: `1px solid ${allSelected ? team.color : team.color + "40"}`, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "all 0.2s" }}>
                          <Users size={14} /> {allSelected ? `Team ${team.name} (Ausgewählt)` : `+ Team ${team.name}`}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(users || []).map(u => {
                    const isSel = currentOverrides.includes(u.id);
                    return (
                      <div key={u.id} onClick={() => { if (!setBetaTeamOverride) return; if (isSel) setBetaTeamOverride(currentOverrides.filter(id => id !== u.id)); else setBetaTeamOverride([...currentOverrides, u.id]); }} style={{ padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, background: isSel ? "#8B5CF6" : T.input, color: isSel ? "#fff" : T.textMuted, border: `1px solid ${isSel ? "#8B5CF6" : T.border2}`, transition: "all 0.2s ease" }}>
                        <div style={{ width: 18, height: 18, borderRadius: "50%", background: u.color || "#444", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9 }}>{initials(u.name)}</div>
                        {u.name} {isSel && <Bell size={12} color="#fff" />}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: T.textFaint, marginTop: 8 }}>Das Standard-Betateam wird immer automatisch benachrichtigt.</div>
              </Field>
              
              <button type="button" onClick={nl2ReleaseBeta} disabled={nl2Uploading || !nl2BetaForm?.version} style={{ width: "100%", background: "#8B5CF6", color: "#fff", border: "none", padding: "14px", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: (nl2Uploading || !nl2BetaForm?.version) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: (nl2Uploading || !nl2BetaForm?.version) ? 0.5 : 1, transition: "all 0.2s", marginTop: 8 }}>
                {nl2Uploading ? <div style={{ width: 18, height: 18, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> : <UploadCloud size={18} />} {nl2Uploading ? "Wird veröffentlicht..." : "Veröffentlichen & Tester benachrichtigen"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* RECHTE SPALTE: LOGBUCH */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, display: "flex", flexDirection: "column", height: "calc(100vh - 160px)" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, background: "rgba(0,0,0,0.1)" }}>
          <History size={20} color={ACCENT_LIGHT} />
          <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Entwicklungs-Logbuch</span>
        </div>
        
        {/* Log-Eingabe */}
        {canWork && (
          <div style={{ padding: 20, borderBottom: `1px solid ${T.border}`, background: T.cardHover }}>
            <div style={{ display: "flex", gap: 10 }}>
              <select value={nl2LogType} onChange={e => setNl2LogType(e.target.value)} style={{ ...iS, width: 140, fontWeight: 600 }}>
                {/* 🚀 NUR NOCH DREI KATEGORIEN */}
                <option value="modelling">Modelling</option>
                <option value="scripting">Scripting</option>
                <option value="others">Sonstiges</option>
              </select>
              <input value={nl2LogText} onChange={e => setNl2LogText(e.target.value)} onKeyDown={e => e.key === "Enter" && nl2AddLog()} placeholder="Eintrag hinzufügen..." style={{ ...iS, flex: 1 }} />
              <button type="button" onClick={nl2AddLog} style={{ background: ACCENT_LIGHT, border: "none", color: "#0A0D15", width: 42, height: 42, borderRadius: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: nl2LogText.trim() ? 1 : 0.5 }}>
                <Send size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Log-Liste */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          {(!nl2Log || nl2Log.length === 0) ? <div style={{ textAlign: "center", color: T.textFaint, padding: 40 }}>Noch keine Einträge.</div> : (nl2Log || []).map(log => {
            const metaBg = log.type === 'modelling' ? '#F59E0B20' : log.type === 'scripting' ? '#34D39920' : '#8B5CF620';
            const metaColor = log.type === 'modelling' ? '#F59E0B' : log.type === 'scripting' ? '#34D399' : '#8B5CF6';
            
            return (
              <div key={log.id} style={{ display: "flex", gap: 14, padding: "14px", border: `1px solid ${T.border2}`, borderRadius: 12, background: T.bg }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: metaBg, color: metaColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {log.type === 'modelling' ? <PenTool size={14}/> : log.type === 'scripting' ? <Terminal size={14}/> : <Paperclip size={14}/>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{log.user_name}</span>
                      <span style={{ fontSize: 11, color: T.textGhost }}>{timeAgo(log.created_at)}</span>
                    </div>
                    {/* 🚀 LÖSCHEN BUTTON - ROBUST & SICHTBAR FÜR ADMINS */}
                    {(isAdmin || log.user_id === currentUser?.id) && (
                      <button type="button" onClick={() => nl2DeleteLog(log.id)} style={{ background: "rgba(255, 71, 87, 0.1)", border: "1px solid rgba(255, 71, 87, 0.2)", color: "#FF4757", cursor: "pointer", padding: "6px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }} title="Eintrag löschen">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{log.text}</div>
                  <div style={{ marginTop: 8 }}>
                    <span style={{ fontSize: 10, background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textFaint, padding: "3px 8px", borderRadius: 6, fontWeight: 600, textTransform: "uppercase" }}>
                      {log.type === 'others' ? 'Sonstiges' : log.type}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* EINSTELLUNGEN MODAL */}
      {showSettingsModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: 500, boxShadow: "0 32px 72px rgba(0,0,0,0.6)" }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 24, display: "flex", alignItems: "center", gap: 8 }}><Settings color="#8B5CF6" /> Einstellungen</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              
              <Field label="BERECHTIGTES TEAM FÜR NL2" T={T}>
                <select value={workingTeamId || ""} onChange={e => setWorkingTeamId(e.target.value || null)} style={iS}>
                  <option value="">Alle Teams (Keine Beschränkung)</option>
                  {(teams || []).map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                </select>
                <div style={{ fontSize: 11, color: T.textFaint, marginTop: 6 }}>Nur Mitglieder dieses Teams (und Admins) können das Projekt sperren und Logs schreiben.</div>
              </Field>

              <Field label="DISCORD BENACHRICHTIGUNGS-KANAL ID" T={T}>
                <input value={discordChannel} onChange={e => setDiscordChannel(e.target.value)} placeholder="z.B. 1234567890123" style={iS} />
              </Field>

              <Field label="STANDARD BETA-TEAM" T={T}>
                {teams && teams.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    {teams.map(team => {
                      const allSelected = team.memberIds && team.memberIds.length > 0 && team.memberIds.every(id => defaultTeamIds.includes(id));
                      return (
                        <button type="button" key={team.id} onClick={() => toggleEntireTeam(team, defaultTeamIds, setDefaultTeamIds)} style={{ padding: "6px 12px", borderRadius: 8, background: allSelected ? team.color : team.color + "20", color: allSelected ? "#fff" : team.color, border: `1px solid ${allSelected ? team.color : team.color + "40"}`, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                          <Users size={14} /> {allSelected ? `Team ${team.name} (Ausgewählt)` : `+ Team ${team.name}`}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: "150px", overflowY: "auto", padding: "4px" }}>
                  {(users || []).map(u => {
                    const isSel = defaultTeamIds.includes(u.id);
                    return (
                      <div key={u.id} onClick={() => isSel ? setDefaultTeamIds(defaultTeamIds.filter(id => id !== u.id)) : setDefaultTeamIds([...defaultTeamIds, u.id])} style={{ padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, background: isSel ? "#34D399" : T.input, color: isSel ? "#0A0A0F" : T.textMuted, border: `1px solid ${isSel ? "#34D399" : T.border2}` }}>
                        <div style={{ width: 18, height: 18, borderRadius: "50%", background: isSel ? "#0A0A0F30" : u.color || "#444", color: isSel ? "#0A0A0F" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9 }}>{initials(u.name)}</div>
                        {u.name}
                      </div>
                    );
                  })}
                </div>
              </Field>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 32 }}>
              <button type="button" onClick={() => setShowSettingsModal(false)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "9px 18px", borderRadius: 9, cursor: "pointer", fontWeight: 600 }}>Abbrechen</button>
              <button type="button" onClick={saveSettings} style={{ background: "#8B5CF6", border: "none", color: "#fff", padding: "9px 22px", borderRadius: 9, fontWeight: 700, cursor: "pointer" }}>Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}